// src/lib/channelsView.ts
//
// «Canales»: which channels each block lists and how a section's median time
// reads. The per-channel table also lists the journeys that ended collecting
// the key at the dispenser without a check-in («Dispensador (solo llave)»);
// the section blocks do not (a dispenser shows no sections).
import { getChannelShortLabelKey } from '@/design/registry/channelRegistry';
import { isInsufficientSample } from '@/lib/analyticsFormat';
import type { StopsBySectionChannelStat } from '@/types/channelAnalytics';
import type { Channel } from '@/types/models';

/** Channels with sections (pre-check-in or check-in screens). */
export const SECTION_CHANNELS: Channel[] = ['Online', 'Kiosk', 'MobileKiosk', 'AirLobby'];

/** Rows of «Por canal», in order. */
export const TABLE_CHANNELS: Channel[] = [...SECTION_CHANNELS, 'Dispenser'];

/** The table rows to show for the channel filter (empty = all). */
export const visibleTableChannels = (selected: Channel[]): Channel[] =>
  selected.length === 0 ? TABLE_CHANNELS : TABLE_CHANNELS.filter((ch) => selected.includes(ch));

/** The section columns to show for the channel filter (empty = all). */
export const visibleSectionChannels = (selected: Channel[]): Channel[] =>
  selected.length === 0 ? SECTION_CHANNELS : SECTION_CHANNELS.filter((ch) => selected.includes(ch));

/** The label of a row of «Por canal». */
export const channelRowLabelKey = (channel: Channel): string =>
  channel === 'Dispenser' ? 'resources.channelsAnalytics.table.dispenserKeyOnly' : getChannelShortLabelKey(channel);

export type StopMedian =
  | { kind: 'value'; seconds: number }
  | { kind: 'insufficient'; count: number }
  | { kind: 'none' };

/**
 * The median time in a section for one channel: the figure when the API sends
 * it, «muestra insuficiente (n = X)» when it withheld it below the minimum of
 * timed visits, nothing when no visit was timed.
 */
export const stopMedian = (stat: StopsBySectionChannelStat | undefined, minimum: number | undefined): StopMedian => {
  if (!stat) return { kind: 'none' };
  if (stat.medianSeconds !== null && stat.medianSeconds !== undefined) return { kind: 'value', seconds: stat.medianSeconds };
  const sample = stat.timedVisits ?? 0;
  return isInsufficientSample(sample, minimum) ? { kind: 'insufficient', count: sample } : { kind: 'none' };
};
